import React from "react";
import { Box, TextField } from "@mui/material";
import { MRT_Row, MRT_ColumnDef } from "material-react-table";
import type { Job } from "../../../../../../../models";
import { ImportTableColumn } from "../../../../../../../utilities/types";
import { getValidationErrorKey } from "../../../../../../../utilities/jobValidation";
import { columnDisplayNames } from "../../../../../../admin/ImportSettings";
import { StringColumnOptions } from "../../../ImportTable.types";
import { ColumnContext } from "../ColumnContext";
import { useTextColumnViewModel } from "./textColumn.vm";
import { TextColumnViewModel } from "./textColumn.types";
import { useValidationErrors } from "../../../shared/hooks/ValidationErrorsContext";
import { useIsRowInEditMode, useEditModeContext } from "../../../shared/hooks/EditModeContext";

interface TextColumnCellProps {
	row: MRT_Row<Job>;
	vm: TextColumnViewModel;
	fieldName: keyof Job;
	columnId: ImportTableColumn;
	inputType?: string;
	isDisabled?: (row: MRT_Row<Job>) => boolean;
}

/**
 * Memoized Cell component for text columns
 * Prevents unnecessary re-renders when column definitions change
 * Uses local state during editing and only syncs to global state on blur
 * This prevents expensive re-renders on every keystroke
 */
const TextColumnCell = React.memo(
	function TextColumnCell({ row, vm, fieldName, columnId, inputType = "text", isDisabled }: TextColumnCellProps) {
		const job = row.original;
		const validationErrors = useValidationErrors();

		// Check if this is a group row (has subRows) - if so, use group editing logic
		const isGroupRow = row.subRows && row.subRows.length > 0;

		// For group rows, check edit mode using row.id (e.g., "grower:Alexander, Curt")
		// For regular rows, check using job.id (UUID)
		const editModeId = isGroupRow ? row.id : job.id;
		const isInEditMode = useIsRowInEditMode(editModeId);
		const { exitRowEdit } = useEditModeContext();
		const errorKey = getValidationErrorKey(columnId, job.id);
		const isRequired = vm.getIsRequired(row);
		const disabled = vm.getIsDisabled(row, isDisabled);
		const hasError = !!validationErrors?.[errorKey];

		// View Mode: render plain text for performance
		if (!isInEditMode) {
			const displayValue = vm.getCurrentValue(job, fieldName);
			return (
				<Box
					title={hasError ? validationErrors[errorKey] : undefined}
					sx={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						color: hasError ? "error.main" : "inherit",
						borderBottom: hasError ? "2px solid" : "none",
						borderColor: "error.main",
						width: "100%",
						px: 0.5,
					}}
				>
					{displayValue || <span style={{ color: "#999" }}>-</span>}
				</Box>
			);
		}

		// Force re-render trigger for local editing state
		const [, forceRender] = React.useState(0);

		// Unique key for this cell's editing state
		const editKey = `${job.id}-${String(fieldName)}`;

		// Check if we have a local editing value (for both group and regular rows)
		const hasEditingValue = cellEditingValues.has(editKey);
		const currentValue = hasEditingValue ? cellEditingValues.get(editKey)! : vm.getCurrentValue(job, fieldName);

		if (isGroupRow) {
			// Group row: update all rows in group on blur
			return (
				<TextField
					size="small"
					type={inputType}
					variant="outlined"
					required={isRequired}
					disabled={disabled}
					fullWidth
					value={currentValue}
					error={!!validationErrors?.[errorKey]}
					helperText={validationErrors?.[errorKey]}
					onChange={(event) => {
						if (disabled) return;
						cellEditingValues.set(editKey, event.target.value);
						forceRender((n) => n + 1);
					}}
					onKeyDown={(e) => {
						if (e.key === "Escape") {
							exitRowEdit(editModeId); // Use row.id for group rows
						}
					}}
					onBlur={() => {
						if (disabled) return;
						const editedValue = cellEditingValues.get(editKey);
						if (editedValue !== undefined && editedValue !== vm.getCurrentValue(job, fieldName)) {
							vm.handleGroupTextChange(row, fieldName, editedValue);
						}
						cellEditingValues.delete(editKey);
					}}
				/>
			);
		}

		// Regular row: use local state during editing, sync on blur
		// This prevents global state updates on every keystroke
		return (
			<TextField
				size="small"
				type={inputType}
				variant="outlined"
				required={isRequired}
				disabled={disabled}
				fullWidth
				value={currentValue}
				error={!!validationErrors?.[errorKey]}
				helperText={validationErrors?.[errorKey]}
				onChange={(event) => {
					if (disabled) return;
					// Store in local editing state instead of updating global state
					cellEditingValues.set(editKey, event.target.value);
					forceRender((n) => n + 1);
				}}
				onKeyDown={(e) => {
					if (e.key === "Escape") {
						exitRowEdit(job.id);
					}
				}}
				onBlur={() => {
					if (disabled) return;
					const editedValue = cellEditingValues.get(editKey);
					if (editedValue !== undefined && editedValue !== vm.getCurrentValue(job, fieldName)) {
						// Sync to global state only on blur
						vm.handleTextChange(job.id, fieldName, editedValue);
					}
					cellEditingValues.delete(editKey);
					vm.handleTextBlur(job, fieldName, row);
				}}
			/>
		);
	},
	(prevProps, nextProps) => {
		const prevJob = prevProps.row.original;
		const nextJob = nextProps.row.original;

		return (
			prevJob.id === nextJob.id &&
			prevJob[prevProps.fieldName] === nextJob[nextProps.fieldName] &&
			prevProps.columnId === nextProps.columnId &&
			prevProps.inputType === nextProps.inputType &&
			prevProps.vm === nextProps.vm
		);
	},
);

interface TextColumnAggregatedCellProps {
	row: MRT_Row<Job>;
	vm: TextColumnViewModel;
	fieldName: keyof Job;
	inputType?: string;
}

// Module-level storage for editing values that survives component remounts
// Used by both regular cells and aggregated cells to prevent global state updates on every keystroke
const cellEditingValues = new Map<string, string>();

/**
 * Memoized AggregatedCell component for text columns
 * Renders editable TextField when group row is in edit mode
 */
const TextColumnAggregatedCell = React.memo(function TextColumnAggregatedCell({
	row,
	vm,
	fieldName,
	inputType = "text",
}: TextColumnAggregatedCellProps) {
	// For group rows, check edit mode using row.id (e.g., "grower:Alexander, Curt")
	const isInEditMode = useIsRowInEditMode(row.id);
	const { exitRowEdit } = useEditModeContext();
	const [, forceRender] = React.useState(0);

	// Get value from first job in group
	const job = row.original;
	const currentStoredValue = vm.getCurrentValue(job, fieldName);

	// Unique key for this cell's editing state
	const editKey = `${row.id}-${String(fieldName)}`;
	const hasEditingValue = cellEditingValues.has(editKey);
	const currentValue = hasEditingValue ? cellEditingValues.get(editKey)! : currentStoredValue;

	// View Mode: render plain text
	if (!isInEditMode) {
		return (
			<Box
				sx={{
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
					width: "100%",
					px: 0.5,
				}}
			>
				{currentStoredValue || <span style={{ color: "#999" }}>-</span>}
			</Box>
		);
	}

	// Edit Mode: render TextField that updates all rows in group on blur
	return (
		<TextField
			size="small"
			type={inputType}
			variant="outlined"
			fullWidth
			value={currentValue}
			onChange={(event) => {
				cellEditingValues.set(editKey, event.target.value);
				forceRender((n) => n + 1);
			}}
			onKeyDown={(e) => {
				if (e.key === "Escape") {
					cellEditingValues.delete(editKey);
					exitRowEdit(row.id);
				}
			}}
			onBlur={() => {
				const editedValue = cellEditingValues.get(editKey);
				if (editedValue !== undefined && editedValue !== currentStoredValue) {
					vm.handleGroupTextChange(row, fieldName, editedValue);
				}
				cellEditingValues.delete(editKey);
			}}
		/>
	);
});

/**
 * Build a text input column with controlled input
 * Uses memoized Cell components to prevent unnecessary re-renders when column definitions change
 */
export function textColumn(
	context: ColumnContext,
	fieldName: keyof Job,
	columnId: ImportTableColumn,
	options: StringColumnOptions = {},
): MRT_ColumnDef<Job, unknown> {
	const { hasGroupUpdate = true, inputType = "text", size, minSize, isDisabled } = options;

	const vm = useTextColumnViewModel(context, columnId);

	return {
		id: columnId,
		header: columnDisplayNames[columnId],
		accessorKey: fieldName,
		enableEditing: false,
		...(size && { size }),
		...(minSize && { minSize }),
		Cell: ({ row }: { row: MRT_Row<Job> }) => (
			<TextColumnCell
				row={row}
				vm={vm}
				fieldName={fieldName}
				columnId={columnId}
				inputType={inputType}
				isDisabled={isDisabled}
			/>
		),
		...(hasGroupUpdate && {
			AggregatedCell: ({ row }: { row: MRT_Row<Job> }) => (
				<TextColumnAggregatedCell row={row} vm={vm} fieldName={fieldName} inputType={inputType} />
			),
		}),
	};
}
